import { OFFLINE_SERVICE, OfflineService } from "@cervval.js/client-html5-core/src/services/offline.service";
import { CLIENT_INFO } from "@cervval.js/client-html5-core/src/tools/clientInfo";
import { Display, setDisplay, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { NAVIGATION_SERVICE, NavigationService } from "@cervval.js/client-html5-navigation/src/navigation.service";
import { generateHrefWithReload, generateHrefWithVersion } from "@cervval.js/client-html5-navigation/src/navigation.tools";
import { ClientRouteOptions, TypedClientAjaxProxy } from "@cervval.js/client-html5-spa/routes";
import { Tooltip } from "@cervval.js/client-html5-ui/src/bootstrap";
import { CjsDialog, DialogPreConfig } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { TOAST_SERVICE, ToastService } from "@cervval.js/client-html5-ui/src/components/toast/toastService/toast.service";
import { ACTION_CACHE_SERVICE, ActionCacheService } from "@cervval.js/common-action/src/services/cache.service";
import { C2S, RoomName, S2C, UserDisplayInfo, WS_ROOM_PREFIX } from "@cervval.js/common-websocket/src/webSocket.consts";
import { WebSocketRoutes } from "@cervval.js/common-websocket/src/webSocket.routes";
import { getRoomPrefixes, isLeavingRoom } from "@cervval.js/common-websocket/src/webSocket.tools";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { LOG_SERVICE, LogService, getLogConsole } from "@cervval.js/common/src/services/log.service";
import { arraysEqualIgnoreOrder } from "@cervval.js/common/src/tools/collection.array.tools";
import { DebugCjs, DebugHandler } from "@cervval.js/common/src/tools/debug.decorator";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { anyReallyAny } from "@cervval.js/common/tools";
import { ManagerOptions, Socket, SocketOptions, io } from "socket.io-client";
import { i18n as ws_i18n } from "./i18n/i18n";

export { Rooms } from "@cervval.js/common-websocket/src/webSocket.consts";
export const CLIENT_WEB_SOCKET_SERVICE = "CLIENT_WEB_SOCKET_SERVICE";

export interface PageSocketData {
    /** page name */
    name: string | undefined;
    /** rooms that are requested by current page (that we will leave on next page navigation) */
    rooms: RoomName[];
    /** users in each room of the page */
    currentUsers?: { [roomName: string]: UserDisplayInfo[] };
    /** page options that are set on page refresh */
    options?: PageSocketOptions;
}

export interface PageSocketOptions {
    /** display users */
    displayUsersInRoom?: {
        /** if div is not provided, you must pass a renderUsersInRoom function */
        div: HTMLElement | undefined,
        /** override default render */
        renderUsersInRoom?: (div: HTMLElement | undefined, data: { users: UserDisplayInfo[], currentUser: UserDisplayInfo | undefined, pageData: PageSocketData }) => void
    };
    /** data update message listener */
    onDataUpdate?: (message: S2C["DATA_UPDATE"]) => void;
}

@Injectable(CLIENT_WEB_SOCKET_SERVICE)
export class ClientWebSocketService {
    /** client socket created and connected in init */
    private _socket: Socket | undefined;

    /** service messages */
    private _i18n = ws_i18n().ClientWebSocketService;

    @DebugCjs()
    protected _debug!: DebugHandler;

    @Injected(OFFLINE_SERVICE, { optional: true })
    protected readonly _offlineService?: OfflineService;

    @Injected(LOG_SERVICE)
    protected readonly _logService!: LogService;

    @Injected(NAVIGATION_SERVICE)
    protected readonly _navigationService!: NavigationService<string>;

    @Injected(ACTION_CACHE_SERVICE, { optional: true })
    protected readonly _actionCacheService?: ActionCacheService<anyReallyAny>;

    @Injected(TOAST_SERVICE)
    private _toastService!: ToastService;

    @TypedClientAjaxProxy()
    public readonly routes!: WebSocketRoutes<ClientRouteOptions>;

    /** Set to true when application updated popup is displayed in order to prevent "double" popup display if socket is reconnected */
    private _applicationUpdatedPopupDisplayed = false;

    /** 
     * The rooms are kept here so that the user enters back the rooms on re-connection (server restarted) 
     * rooms not linked to the current page (ie that persist during navigation)
     */
    private _applicationRooms: RoomName[] = [];

    /** Data associated to the current page, cleared on each navigation and usually set at the begin of pages refresh (but can be set at any time) */
    private readonly _currentPageData: PageSocketData = {
        name: undefined,
        rooms: []
    };

    /** Flag to enable or disable the automatic check on user's login if the socket is disconnected */
    protected _checkLoginOnDisconnect = true;

    /** display an error in the console */
    protected _notRegistered(): void {
        this._logService.error("Socket is not registered yet");
    }

    /**
     * @returns the socket uid if connected, undefined otherwise
     */
    public getSocketUid(): string | undefined {
        return this._socket?.id;
    }

    /** 
     * @returns the current page's room
     */
    public get pageRooms(): ReadonlyArray<RoomName> {
        return [...this._currentPageData.rooms];
    }

    /** 
     * @returns the application's room
     */
    public get applicationRooms(): ReadonlyArray<RoomName> {
        return [...this._applicationRooms];
    }

    /** @returns all the rooms from application rooms and page rooms prefixed with the given prefix */
    public getRoomsWithPrefix(prefix: WS_ROOM_PREFIX): RoomName[] {
        const prefixedRooms: string[] = [];
        for (const room of [...this._applicationRooms, ...this._currentPageData.rooms]) {
            if (!getRoomPrefixes(room).has(prefix)) { continue; }
            prefixedRooms.push(room);
        }
        return prefixedRooms;
    }

    /** 
     * Enable or disable the automatic check on user's login when the socket is disconnected.
     * (Enabled by default)
     * 
     * This is mostly useful if don't want user to be redirected by this mechanism, especially on logout.
     */
    public setCheckLoginOnDisconnect(enable: boolean): void {
        this._checkLoginOnDisconnect = enable;
    }

    /** init socket and install associated listeners */
    public init(options?: {
        uri?: string,
        opts?: Partial<ManagerOptions & SocketOptions>,
        webSocketPath?: string,
        onConnectCallback?: () => void
    }): void {
        this._debug("init");

        // init socket
        const socket = io(options?.uri ?? "/", {
            ...options?.opts,
            reconnection: true,
            path: options?.webSocketPath ?? window.location.pathname + "socket.io",
            // enable only websocket transport (meaning there is no fallback to polling) so that load balancing works without sticky session
            // https://socket.io/docs/v4/using-multiple-nodes
            transports: ["websocket"]
        });
        this._socket = socket;

        // subscrive to navigation service to emit messages on each navigation and update current page data and options
        this._handleNavigations();
        // listen to standard server to client messages and trigger current page callbacks
        this._listenToStandardMessages();

        this._socket.on("connect", () => {
            this._debug("connect socketUid", this.getSocketUid());
            if (this._navigationService.currentPage) {
                // we just connected => emit navigation
                this.emitC2S("USER_NAVIGATION", { page: String(this._navigationService.currentPage) });
            }
            // leave all rooms and (re)join current rooms
            this._registerRooms({ leaveRooms: ["*"], enterRooms: this._currentPageData.rooms.concat(this._applicationRooms) });

            options?.onConnectCallback?.();
        });

        // install debug
        this._instrumentDebugIfEnabled(this._socket);

        // notify offline service on connection success / failure if OFFLINE_SERVICE is registered
        injector.executeWhenRegistered(OFFLINE_SERVICE, () => {
            this._instrumentOffline(socket);
        });

    }

    /**
     * notify offline service on connection success / failure 
     */
    protected _instrumentOffline(socket: Socket): void {
        // success
        for (const message of ["connect", "reconnect"]) {
            socket.on(message, (...args) => {
                this._offlineService?.setOfflineStatus({
                    offline: false,
                    origin: CLIENT_WEB_SOCKET_SERVICE
                });
            });
        }
        // failure
        for (const message of ["connect_timeout", "connect_error", "reconnect_error"]) {
            socket.on(message, (...args) => {
                this._offlineService?.setOfflineStatus({
                    offline: true,
                    origin: CLIENT_WEB_SOCKET_SERVICE
                });
            });
        }
    }

    /**
     * listen to socket io events if debug is enabled
     */
    protected _instrumentDebugIfEnabled(socket: Socket): void {
        if (this._debug.enabled) {
            for (const message of ["connect", "disconnect", "connect_error", "connect_timeout", "reconnection_attempt", "reconnect", "reconnect_error"]) {
                socket.on(message, (...args) => {
                    this._debug(message, ...args);
                });
            }
            // ! Acknowledgements are not caught in the catch-all listener.
            socket.prependAny((...args) => {
                this._debug("received", ...args);
            });
        }
    }

    /** emit message with data on provided socket */
    public emit<ClientToServerMessages, Msg extends keyof ClientToServerMessages>(message: Msg, data: ClientToServerMessages[Msg]): void {
        if (this._socket) {
            this._debug("emit", message, data);
            this._socket.emit(String(message), data);
        } else {
            this._notRegistered();
        }
    }

    /** emit one of the common Client2Server messages */
    public emitC2S<Msg extends keyof C2S>(message: Msg, data: C2S[Msg]): void {
        this.emit<C2S, Msg>(message, data);
    }

    /** add callback for message */
    public on<ServerToClientMessages, Msg extends keyof ServerToClientMessages>(message: Msg, callback: (data: ServerToClientMessages[Msg]) => void): void {
        // remove callback first to prevent double events 
        // don't remove ! We assume this is there in the code, for example removing this makes connections page go in infinite loop
        this.off(message, callback);

        if (this._socket) {
            this._socket.on(String(message), callback);
        } else {
            this._notRegistered();
        }
    }

    /** add callback for Server2Client message */
    public onS2C<Msg extends keyof S2C>(message: Msg, callback: (data: S2C[Msg]) => void): void {
        this.on<S2C, Msg>(message, callback);
    }

    /** remove callback for message */
    public off<ServerToClientMessages, Msg extends keyof ServerToClientMessages>(message: Msg, callback: (data: ServerToClientMessages[Msg]) => void): void {
        if (this._socket) {
            this._socket.off(String(message), callback);
        }
    }

    /** remove callback for Server2Client message */
    public offS2C<Msg extends keyof S2C>(message: Msg, callback: (data: S2C[Msg]) => void): void {
        this.off<S2C, Msg>(message, callback);
    }

    /** if displayUsersInRoom option is set, hide the associated div */
    protected _hideUsersInRoomDiv(): void {
        if (this._currentPageData.options?.displayUsersInRoom?.div) {
            // hide div before options are cleared
            setDisplay(this._currentPageData.options.displayUsersInRoom.div, Display.NONE);
        }
    }

    /**
     * Listen to navigation change and emit message to server
     */
    protected _handleNavigations(): void {
        this._navigationService.beforeNavigation.on((p) => {
            if (p.detail.previous !== p.detail.next) {
                // we are on a new page, we reset all current page data
                this._currentPageData.name = String(p.detail.next);
                this._currentPageData.currentUsers = undefined;
                // setPageRooms needs to be done before setPageOptions in order to hide previous usersInRoom div
                this.setPageRooms([]);
                this.setPageOptions(undefined);
                if (this._socket) {
                    // notify server (for connections page and stats) (notify after options change to prevent getting misleading notifications)
                    this.emitC2S("USER_NAVIGATION", { page: String(p.detail.next) });
                }
            }
        });
    }

    /** Set options for current page */
    public setPageOptions(options: PageSocketOptions | undefined): void {
        this._currentPageData.options = options;
    }

    /** 
     * Enter in page rooms
     * Rooms can have prefixes that enables distinct behaviors @see{@link WS_ROOM_PREFIX}
     * 
     * To make sure the room names are well formatted, use @see{@link generateRoomName}
     * @returns true if rooms changed
     */
    public setPageRooms(rooms: RoomName[]): boolean {
        if (arraysEqualIgnoreOrder(rooms, this._currentPageData.rooms)) {
            // nothing to do
            return false;
        }

        this._debug("pageRooms", rooms);


        // reset current users display
        this._hideUsersInRoomDiv();

        this._registerRooms({ leaveRooms: this._currentPageData.rooms, enterRooms: rooms });
        this._currentPageData.rooms = [...rooms];
        return true;
    }

    /** 
     * Enter in app rooms
     * Rooms can have prefixes that enables distinct behaviors @see{@link WS_ROOM_PREFIX}
     * 
     * To make sure the room names are well formatted, use @see{@link generateRoomName} 
     */
    public registerApplicationRooms(rooms: { leaveRooms?: RoomName[], enterRooms?: RoomName[] }): void {
        this._debug("applicationRooms", rooms);

        this._registerRooms(rooms);

        // update _applicationRooms
        const appRooms = new Set(this._applicationRooms.filter((r) => {
            if (rooms.leaveRooms && isLeavingRoom({ roomToTest: r, roomsToLeave: rooms.leaveRooms })) {
                return false;// remove
            }
            return true;
        }));
        if (rooms.enterRooms) {
            for (const r of rooms.enterRooms) {
                appRooms.add(r);
            }
        }
        this._applicationRooms = Array.from(appRooms);
    }

    /**
     * Client can leave rooms A_B* and then enter rooms A_B_1, A_B_2
     */
    protected _registerRooms(rooms: { leaveRooms?: RoomName[], enterRooms?: RoomName[] }): void {
        if (this._socket) {
            const roomsMessage = { leaveRooms: rooms.leaveRooms ?? [], enterRooms: rooms.enterRooms ?? [] };
            // filter rooms that are both in leave and enter
            const intersection = roomsMessage.leaveRooms.filter(r => roomsMessage.enterRooms.includes(r));
            if (intersection?.length) {
                roomsMessage.leaveRooms = roomsMessage.leaveRooms.filter(r => !intersection.includes(r));
                roomsMessage.enterRooms = roomsMessage.enterRooms.filter(r => !intersection.includes(r));
            }

            // Remove concurrent users data from leaved rooms only 
            if (this._currentPageData.currentUsers != null) {
                for (const r of roomsMessage.leaveRooms) {
                    delete this._currentPageData.currentUsers[r];
                }
            }

            this._debug("registerRooms", "leave", roomsMessage.leaveRooms, "enter", roomsMessage.enterRooms);
            if (roomsMessage.leaveRooms.length || roomsMessage.enterRooms.length) {
                this.emitC2S("USER_ROOMS", roomsMessage);
            }
        } else {
            this._notRegistered();
        }
    }

    /**
     * Listen to standard server to client messages and trigger current page callbacks
     */
    protected _listenToStandardMessages(): void {
        // check user is still logged in this browser tab
        this._listenToLogoutMessage();
        // show toast on app version change
        this._listenToVersionChange();
        // show message and reload app on session termination
        this._listenToSessionTermination();
        this._listenToDataUpdate();
        this._listenToRoomMembers();
    }

    /** check user is still logged in this browser tab */
    protected _listenToLogoutMessage(): void {
        // when user logs out, the server sends a LOGGED_OUT message
        // it does not mean the current browser tab is concerned (user could be connected on another device or browser)
        this.onS2C("LOGGED_OUT", () => {

            if (this._checkLoginOnDisconnect) {
                // this call will redirect to login page if user is not logged in anymore
                this.routes.checkLoggedIn(undefined).catch(getLogConsole().error);
            }
        });
    }

    /** Show a message and reload application */
    protected _onSessionTerminated(): void {
        const dialog = new CjsDialog({
            preConfig: DialogPreConfig.NO_BUTTON,
            enableDismiss: false,
            title: this._i18n.sessionTerminated.title
        });// no need to append the dialog to the DOM, it adds itself automatically

        // TODO allow user to reconnect (maybe in a another tab) and send pending actions
        const href = generateHrefWithReload(location.href, NOW());
        dialog.addToBody("<a href=\"" + href + "\">" + this._i18n.sessionTerminated.link + "</a>");
    }

    /** Show a message and reload application on message received */
    protected _listenToSessionTermination(): void {
        this.onS2C("SESSION_TERMINATED", () => {
            this._onSessionTerminated();
        });
    }

    /** Show a toast when a new version of the application is detected */
    protected _listenToVersionChange(): void {
        this.onS2C("NEW_SOCKET", (appInfos: { appVersion: string }) => {
            if (!this._applicationUpdatedPopupDisplayed && appInfos) {
                const clientVersion: string = CLIENT_INFO.appVersion;
                if (clientVersion && clientVersion < appInfos.appVersion) {// < to allow a 'client only' patch to be deployed
                    this._applicationUpdatedPopupDisplayed = true;
                    const href = generateHrefWithVersion(location.href, appInfos.appVersion);
                    if (href !== location.href) {
                        this._toastService.show({
                            title: this._i18n.applicationWasUpdated.title,
                            content: "<a href=\"" + href + "\">" + this._i18n.applicationWasUpdated.link + "</a>",
                            delay: undefined,
                            autohide: false,
                            enableDismiss: false
                        });
                    } else {
                        getLogConsole().error("Server version is " + appInfos.appVersion + ", Client version is " + clientVersion + " - reload failed");
                    }
                }
            }
        });
    }

    /**
     * Listen to data update messages and trigger current page callbacks
     */
    protected _listenToDataUpdate(): void {
        this.onS2C("DATA_UPDATE", (data) => {
            // trigger onDataUpdate on current page if requested
            if (this._currentPageData.options?.onDataUpdate) {
                this._currentPageData.options?.onDataUpdate(data);
            }
            if (this._actionCacheService && data.updates?.length) {
                let markedDirty = false;
                for (const u of data.updates) {
                    for (const elementTypeName in u) {
                        const repo = this._actionCacheService.findDTOCacheRepository(elementTypeName);
                        if (repo) {
                            repo.markDirty(u[elementTypeName]);
                            markedDirty = true;
                        }
                    }
                }
                if (markedDirty) {
                    this._actionCacheService.dirtyStateChanged.emit();
                }
            }
        });

    }

    /**
     * Listen to room users messages and trigger current page callbacks
     */
    protected _listenToRoomMembers(): void {
        this.onS2C("ROOM_MEMBERS", (data) => {
            // record users
            this._currentPageData.currentUsers ??= {};
            this._currentPageData.currentUsers[data.room] = data.users;

            // trigger onCacheChange on current page if requested
            const d = this._currentPageData.options?.displayUsersInRoom;
            if (d) {
                // identify unique users in all rooms
                const usersBySocketUid = new Map<string, UserDisplayInfo>();
                for (const room of this._currentPageData.rooms) {
                    const roomUsers = this._currentPageData.currentUsers[room];
                    if (roomUsers) {
                        for (const user of roomUsers) {
                            usersBySocketUid.set(user.socketUid, user);
                        }
                    }
                }
                const users = Array.from(usersBySocketUid.values());
                // identify current user
                const socketUid = this.getSocketUid();
                const currentUser = socketUid ? usersBySocketUid.get(socketUid) : undefined;

                // render users
                if (d.renderUsersInRoom) {
                    // a custom render is provided
                    d.renderUsersInRoom(d.div, { users, currentUser, pageData: this._currentPageData });
                } else if (d.div) {
                    // default render
                    defaultRenderUsersInRoom(d.div, { users, currentUser, pageData: this._currentPageData });
                } else {
                    // else => invalid options
                    getLogConsole().warn("Invalid displayUsersInRoom options");
                }
            }
        });
    }
}

/** default render or "users in room" that displays the user count in a badge with user names as the tooltip */
export function defaultRenderUsersInRoom(div: HTMLElement, data: { users: UserDisplayInfo[], currentUser: UserDisplayInfo | undefined, pageData: PageSocketData }): void {
    // default render
    const displayNamesSet = new Set(data.users.map(u => u.displayName));
    if (data.currentUser?.displayName) {
        displayNamesSet.delete(data.currentUser?.displayName);
    }
    div.innerHTML = String(displayNamesSet.size);
    new Tooltip(div, { title: Array.from(displayNamesSet).join("\n") });
    toggleVisible(div, displayNamesSet.size > 0, Display.INLINE_BLOCK);
}
